/**
 * Catálogo de anexos do PCMSO e do PGR (#520). Global: gestores leem, só quem administra a
 * plataforma escreve. A lista é curta e completa — filtro por documento, sem paginação.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Chip, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { DataTable, PageShell, RouteActionButton, type DataTableColumn } from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { documentAttachmentService } from '../../services/documentAttachmentService';
import { getApiErrorMessage } from '../../types/common';
import {
  ROTULO_DO_DOCUMENTO,
  TIPOS_COM_ANEXO,
  type DocumentAttachment,
  type DocumentAttachmentType,
} from '../../types/documentAttachment';

const columns: DataTableColumn<DocumentAttachment>[] = [
  {
    id: 'documentType',
    label: 'Documento',
    render: (anexo) => <Typography variant="body2">{ROTULO_DO_DOCUMENTO[anexo.documentType]}</Typography>,
  },
  {
    id: 'numeral',
    label: 'Nº',
    render: (anexo) => <Typography variant="body2">{anexo.numeral ?? '—'}</Typography>,
  },
  {
    id: 'title',
    label: 'Título',
    primary: true,
    render: (anexo) => (
      <Typography variant="body2" fontWeight="medium">
        {anexo.title}
      </Typography>
    ),
  },
  {
    id: 'normReference',
    label: 'Norma',
    render: (anexo) => <Typography variant="body2">{anexo.normReference ?? '—'}</Typography>,
  },
  {
    id: 'isActive',
    label: 'Situação',
    render: (anexo) => (
      <Chip label={anexo.isActive ? 'Ativo' : 'Inativo'} size="small" color={anexo.isActive ? 'success' : 'default'} />
    ),
  },
];

export const DocumentAttachmentListPage: React.FC = () => {
  const navigate = useNavigate();
  const editar = useControle('/document-attachments/:id/edit', pode('document-attachment.update'), 'Editar');
  const excluir = useAcao('document-attachment.delete', 'Excluir');
  const [anexos, setAnexos] = useState<DocumentAttachment[]>([]);
  const [documento, setDocumento] = useState<DocumentAttachmentType | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      setAnexos(await documentAttachmentService.getAll());
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao carregar o catálogo de anexos'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const visiveis = useMemo(
    () => (documento === '' ? anexos : anexos.filter((anexo) => anexo.documentType === documento)),
    [anexos, documento]
  );

  const handleDelete = async (anexo: DocumentAttachment) => {
    if (!window.confirm(`Excluir o anexo "${anexo.title}" do catálogo? As emissões antigas continuam dizendo que o levaram.`)) return;

    try {
      setError(null);
      await documentAttachmentService.delete(anexo.id);
      carregar();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao excluir o anexo'));
    }
  };

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Anexos dos Documentos' }]}
      title="Anexos dos documentos"
      subtitle="Textos que podem sair no fim do PCMSO e do PGR. Quem emite marca, a cada emissão, quais entram."
      actions={
        <RouteActionButton
          para="/document-attachments/new"
          exige={pode('document-attachment.create')}
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
        >
          Novo anexo
        </RouteActionButton>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2, maxWidth: 240 }}>
        <FormControl fullWidth size="small">
          <InputLabel id="filtro-documento">Documento</InputLabel>
          <Select
            labelId="filtro-documento"
            label="Documento"
            value={documento}
            onChange={(event: SelectChangeEvent<DocumentAttachmentType | ''>) =>
              setDocumento(event.target.value as DocumentAttachmentType | '')
            }
          >
            <MenuItem value="">Todos</MenuItem>
            {TIPOS_COM_ANEXO.map((tipo) => (
              <MenuItem key={tipo} value={tipo}>
                {ROTULO_DO_DOCUMENTO[tipo]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <DataTable
        columns={columns}
        rows={visiveis}
        getRowId={(anexo) => anexo.id}
        loading={loading}
        filtersActive={documento !== ''}
        onClearFilters={() => setDocumento('')}
        emptyState={{
          title: 'Nenhum anexo cadastrado no catálogo ainda',
          description:
            "Cada anexo é um texto pronto que quem emite o PCMSO ou o PGR marca na emissão, e ele sai no fim do documento, em página própria. Quem administra a plataforma cadastra pelo botão 'Novo anexo'.",
        }}
        actions={[
          {
            icon: <EditIcon fontSize="small" />,
            onClick: (anexo) => navigate(`/document-attachments/${anexo.id}/edit`),
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: handleDelete,
            ...excluir,
          },
        ]}
      />
    </PageShell>
  );
};

export default DocumentAttachmentListPage;
